import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Shell from './components/Shell';
import { Spinner } from './components/ui';
import Login from './pages/Login';
import Overview from './pages/Overview';
import Registrations from './pages/Registrations';
import PlanRequests from './pages/PlanRequests';
import Agencies from './pages/Agencies';
import AgencyDetail from './pages/AgencyDetail';
import Onboard from './pages/Onboard';
import Plans from './pages/Plans';
import Billing from './pages/Billing';
import Settings from './pages/Settings';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true, // shared state, edited by a small team
      retry: 1,
    },
  },
});

function FullPageSpinner() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper)',
      }}
    >
      <Spinner large />
    </div>
  );
}

/** Waits for the initial `me` check — redirecting first would bounce a refresh. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { admin, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!admin) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { admin, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <Login />
                </RedirectIfAuthed>
              }
            />
            <Route
              element={
                <RequireAuth>
                  <Shell />
                </RequireAuth>
              }
            >
              <Route index element={<Overview />} />
              <Route path="registrations" element={<Registrations />} />
              <Route path="plan-requests" element={<PlanRequests />} />
              <Route path="agencies" element={<Agencies />} />
              <Route path="agencies/:id" element={<AgencyDetail />} />
              <Route path="onboard" element={<Onboard />} />
              <Route path="plans" element={<Plans />} />
              <Route path="billing" element={<Billing />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
